import dayjs from "dayjs";
import {
  computeOriginalPricePerNight,
  computeOriginalTotalFromPromo,
  ensureExhaustive,
  getPriceString,
  ICategory,
  IconName,
  PromotionAvailability,
  IRewards,
} from "halifax";
import {
  CorpLodgingData,
  DateRange,
  FiatPrice,
  GuestsSelection,
  Lodging,
  LodgingCollectionEnum,
  LodgingMediaAsset,
  PolicyCompliance,
  RewardsAccount,
  RewardsCurrency,
  ValidRewardsCurrency,
  SearchedPremiumOffersProperties,
  PremiumOffersFilterType,
} from "redmond";
import { RoomsSelection } from "redmond/hotels-module/interfaces";
import { hotelStarRatingToNumber } from "redmond/hotels-module/utils";
import { createSelector } from "reselect";
import {
  SORT_PRICE_HIGH_TO_LOW,
  SORT_PRICE_LOW_TO_HIGH,
  SORT_RECOMMENDED,
} from "../../../../lang/textConstants";
import { IStoreState } from "../../../../reducers/types";
import { SORT_OPTIONS, SortOption } from "../index";
import { REQUEST_STATUS } from "../requestState";

export const OFFERS_STATES = {
  LOADING: "loading",
  ERROR: "error",
  SUCCESS: "success",
} as const;

export const getOffersLodgingPromotionResponse = (state: IStoreState) =>
  state.premiumCollectionOffersSearch.premiumOffersShopCall;

export const getIsUserTriggeredSearch = (state: IStoreState) =>
  state.premiumCollectionOffersSearch.isUserTriggeredSearch;

export interface LodgingPriceInfo {
  strikethroughPricePerNight: string | undefined;
  pricePerNight: string;
  strikethroughPriceTotal: string | undefined;
  priceTotal: string;
  totalPriceNumeric: number;
  propertyFees?: string;
}

export interface LodgingPromotionInfo {
  promotionStartDate: dayjs.Dayjs;
  promotionEndDate: dayjs.Dayjs;
  /**
   * The current number of nights for the stay.
   */
  nights: number;
  /**
   * The minimum number of nights required to qualify for the promotion.
   */
  minNights: number;
  vibeTags: string[];
  isLocked: boolean;
}

export interface LodgingRewardsInfo {
  rewardsCurrency: RewardsCurrency;
  totalPriceInMiles: string;
}

export interface LodgingShopInfo {
  available: boolean;
  opaqueShopRequest: string;
}

// Core lodging info that's always available
export interface CoreLodgingInfo {
  index: number;
  lodgingId: string;
  lodgingCollection: LodgingCollectionEnum;
  images: LodgingMediaAsset[];
  lodgingName: string;
  lodgingRating: number;
  lodgingCity: string;
  originalLodging: Lodging;
  policyCompliance?: PolicyCompliance;
}

// Sealed class pattern for different lodging states
export type ProcessedLodgingForOffers =
  | {
      state: PromotionAvailability.HotelUnavailable;
      core: CoreLodgingInfo;
      promotion: LodgingPromotionInfo | undefined;
      shop: LodgingShopInfo;
    }
  | {
      state: PromotionAvailability.PromotionUnavailable;
      core: CoreLodgingInfo;
      prices: LodgingPriceInfo;
      promotion: LodgingPromotionInfo;
      rewards: LodgingRewardsInfo;
      shop: LodgingShopInfo;
    }
  | {
      state: PromotionAvailability.PromotionAvailable;
      core: CoreLodgingInfo;
      prices: LodgingPriceInfo;
      promotion: LodgingPromotionInfo;
      rewards: LodgingRewardsInfo;
      shop: LodgingShopInfo;
    };

// Sealed class pattern for the overall offers state
export type OffersState =
  | { state: typeof OFFERS_STATES.LOADING }
  | { state: typeof OFFERS_STATES.ERROR; error: Error }
  | {
      state: typeof OFFERS_STATES.SUCCESS;
      lodgings: ProcessedLodgingForOffers[];
      shopNavigationParams: {
        stayDates: DateRange;
        guests: Omit<GuestsSelection, "rooms">;
        rooms?: RoomsSelection;
      };
      filterCategories: ICategory[];
    };

export const getSelectedSortOption = (state: IStoreState): SortOption =>
  state.premiumCollectionOffersSearch.sort;

export interface SortOptionItem {
  label: string;
  value: SortOption;
}

export const getSortOptions = (): SortOptionItem[] => [
  {
    label: SORT_RECOMMENDED,
    value: SORT_OPTIONS.RECOMMENDED,
  },
  {
    label: SORT_PRICE_LOW_TO_HIGH,
    value: SORT_OPTIONS.PRICE_ASC,
  },
  {
    label: SORT_PRICE_HIGH_TO_LOW,
    value: SORT_OPTIONS.PRICE_DESC,
  },
];

/**
 * Sorts lodgings per State, then by the selected sort.
 * Always show the Available promotions first, then the Unavailable, then the HotelUnavailable.
 */
export const sortLodgingsForOffers = (
  lodgings: ProcessedLodgingForOffers[],
  sortOption: SortOption
): ProcessedLodgingForOffers[] => {
  const available = lodgings.filter(
    (l) => l.state === PromotionAvailability.PromotionAvailable
  );
  const unavailable = lodgings.filter(
    (l) => l.state === PromotionAvailability.PromotionUnavailable
  );
  const hotelUnavailable = lodgings.filter(
    (l) => l.state === PromotionAvailability.HotelUnavailable
  );

  const sortByPrice = (
    a: ProcessedLodgingForOffers,
    b: ProcessedLodgingForOffers,
    ascending: boolean
  ) => {
    const getPriceValue = (lodging: ProcessedLodgingForOffers): number => {
      if (lodging.state === PromotionAvailability.HotelUnavailable) {
        return ascending ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;
      }

      const numericPrice = lodging.prices?.totalPriceNumeric;
      if (numericPrice === undefined) {
        return ascending ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;
      }

      return numericPrice;
    };

    const priceA = getPriceValue(a);
    const priceB = getPriceValue(b);

    return ascending ? priceA - priceB : priceB - priceA;
  };

  switch (sortOption) {
    case SORT_OPTIONS.RECOMMENDED: {
      // Keep natural order within each state group
      return [...available, ...unavailable, ...hotelUnavailable];
    }

    case SORT_OPTIONS.PRICE_ASC: {
      return [
        ...available.sort((a, b) => sortByPrice(a, b, true)),
        ...unavailable.sort((a, b) => sortByPrice(a, b, true)),
        ...hotelUnavailable,
      ];
    }

    case SORT_OPTIONS.PRICE_DESC: {
      return [
        ...available.sort((a, b) => sortByPrice(a, b, false)),
        ...unavailable.sort((a, b) => sortByPrice(a, b, false)),
        ...hotelUnavailable,
      ];
    }

    default:
      return ensureExhaustive(sortOption);
  }
};

/**
 * Returns an icon for a given vibe tag.
 */
export const getVibeTagIcon = (tag: string): ICategory["icon"] => {
  if (tag === "Outdoors") {
    return IconName.Binoculars;
  }
  if (tag === "City") {
    return IconName.City;
  }
  if (tag === "Wellness") {
    return IconName.Relaxation;
  }
  if (tag === "Arts & Culture") {
    return IconName.ArtAndCulture;
  }
  if (tag === "Beach") {
    return IconName.Beach;
  }
  return undefined;
};

export const getVibeTagFilterCategories = (
  lodgings: ProcessedLodgingForOffers[]
): ICategory[] => {
  if (lodgings.length === 0) {
    return [];
  }

  const allTags = lodgings.flatMap(
    (lodging: ProcessedLodgingForOffers) => lodging.promotion?.vibeTags || []
  );
  const tagCounts = allTags.reduce<Record<string, number>>((acc, tag) => {
    acc[tag] = (acc[tag] || 0) + 1;
    return acc;
  }, {});
  const uniqueTags = Object.keys(tagCounts);
  const categories: ICategory[] = uniqueTags.map((tag) => ({
    label: tag,
    icon: getVibeTagIcon(tag),
    count: tagCounts[tag],
  }));
  return [
    { label: PremiumOffersFilterType.ALL, count: lodgings.length },
    ...categories,
  ];
};

export const getSelectedFilterCategory = (
  state: IStoreState
): PremiumOffersFilterType =>
  state.premiumCollectionOffersSearch.selectedFilterCategory;

export const filterLodgingsForOffers = (
  lodgings: ProcessedLodgingForOffers[],
  selectedFilterCategory: PremiumOffersFilterType
): ProcessedLodgingForOffers[] =>
  selectedFilterCategory === PremiumOffersFilterType.ALL
    ? lodgings
    : lodgings.filter((lodging) =>
        lodging.promotion?.vibeTags?.includes(selectedFilterCategory)
      );

/**
 * Returns price and rewards info for a lodging, matching UI logic and legal compliance.
 * @param lodging The lodging object
 * @param nights Number of nights for the stay
 * @param propertyFee Optional property fee (FiatPrice)
 * @param freeNights Number of free nights (for nth-night promos, default 1)
 */
const getPriceAndRewards = (
  lodging: Lodging,
  nights: number,
  propertyFee?: FiatPrice,
  freeNights: number = 1
) => {
  if (!lodging.price) return { prices: undefined, rewards: undefined };

  const total =
    lodging.price.totalDiscountAware?.priceWithUnmanagedDiscounts ||
    lodging.price.totalPrice;

  const nightly =
    lodging.price.nightlyDiscountAware?.priceWithUnmanagedDiscounts ??
    lodging.price.nightlyPrice;

  let strikethroughNightly;
  if (nightly && nights && freeNights > 0) {
    const origNight = computeOriginalPricePerNight(
      nightly.fiat,
      nights,
      freeNights
    ).perNight;
    strikethroughNightly = origNight;
  }

  const getTotalForStrikethrough = () => {
    if (!lodging.price) return undefined;

    return (
      lodging.price.breakdown.totalExcludingTaxes ??
      lodging.price.breakdown.total
    );
  };

  let strikethroughTotal;
  if (nights && freeNights > 0) {
    const totalForCalculation = getTotalForStrikethrough();
    if (totalForCalculation) {
      strikethroughTotal = computeOriginalTotalFromPromo(
        totalForCalculation.fiat,
        nights,
        freeNights,
        propertyFee
      );
    }
  }

  const totalPriceValue = propertyFee
    ? total.fiat.value + propertyFee.value
    : total.fiat.value;

  let rewards;
  if (total?.rewards) {
    const rewardKey = Object.keys(total.rewards)[0];
    const reward = total.rewards[rewardKey];
    if (
      reward &&
      ValidRewardsCurrency.includes(reward.currency as RewardsCurrency)
    ) {
      rewards = reward;
    }
  }

  let prices;
  if (nightly && total && strikethroughNightly) {
    prices = {
      strikethroughPricePerNight: getPriceString({
        price: strikethroughNightly.value,
        currencySymbol: strikethroughNightly.currencySymbol,
      }),
      pricePerNight: getPriceString({
        price: nightly.fiat.value,
        currencySymbol: nightly.fiat.currencySymbol,
      }),
      strikethroughPriceTotal: strikethroughTotal
        ? getPriceString({
            price: strikethroughTotal.value,
            currencySymbol: strikethroughTotal.currencySymbol,
          })
        : undefined,
      priceTotal: getPriceString({
        price: totalPriceValue,
        currencySymbol: total.fiat.currencySymbol,
      }),
      totalPriceNumeric: totalPriceValue,
      propertyFees: propertyFee
        ? getPriceString({
            price: propertyFee.value,
            currencySymbol: propertyFee.currencySymbol,
          })
        : undefined,
    };
  } else if (nightly && total) {
    prices = {
      strikethroughPricePerNight: undefined,
      pricePerNight: getPriceString({
        price: nightly.fiat.value,
        currencySymbol: nightly.fiat.currencySymbol,
      }),
      strikethroughPriceTotal: undefined,
      priceTotal: getPriceString({
        price: totalPriceValue,
        currencySymbol: total.fiat.currencySymbol,
      }),
      totalPriceNumeric: totalPriceValue,
      propertyFees: propertyFee
        ? getPriceString({
            price: propertyFee.value,
            currencySymbol: propertyFee.currencySymbol,
          })
        : undefined,
    };
  } else {
    prices = undefined;
  }

  return { prices, rewards };
};

export const getOffersState = createSelector(
  [getOffersLodgingPromotionResponse],
  (response): OffersState => {
    switch (response.status) {
      case REQUEST_STATUS.NOT_CALLED:
      case REQUEST_STATUS.IN_PROGRESS:
        return { state: OFFERS_STATES.LOADING };

      case REQUEST_STATUS.FAILED:
        return { state: OFFERS_STATES.ERROR, error: response.error };

      case REQUEST_STATUS.SUCCESS: {
        const { data, params } = response;
        const lodgings: ProcessedLodgingForOffers[] = [];

        const shopNavigationParams = {
          stayDates: params.stayDates,
          guests: params.guests,
          rooms: params.rooms,
        };

        data.lodgings.forEach((lodging, index) => {
          const core: CoreLodgingInfo = {
            index,
            lodgingId: lodging.lodging.id,
            lodgingCollection:
              lodging.lodgingCollection || LodgingCollectionEnum.NoCollection,
            images: lodging.lodging.media,
            lodgingName: lodging.lodging.name,
            lodgingRating: hotelStarRatingToNumber(lodging.lodging.starRating),
            lodgingCity: lodging.lodging.city,
            originalLodging: lodging,
            policyCompliance:
              "corporateTravel" in lodging.lodging
                ? (lodging.lodging as CorpLodgingData).corporateTravel
                    .policyCompliance
                : undefined,
          };

          // Validate required fields and track invalid lodgings
          if (
            !lodging.lodgingCollection ||
            lodging.lodgingCollection === LodgingCollectionEnum.NoCollection
          ) {
            console.warn(
              `Invalid lodging (no collection): ${lodging.lodging.name} (${lodging.lodging.id})`
            );
            return;
          }

          const shop: LodgingShopInfo = {
            available: lodging.available ?? false,
            opaqueShopRequest: lodging.price?.opaqueShopRequest ?? "",
          };

          if (
            !lodging.nthNightPromotion ||
            !lodging.nthNightPromotion.promotionStartDate ||
            !lodging.nthNightPromotion.promotionEndDate
          ) {
            lodgings.push({
              state: PromotionAvailability.HotelUnavailable,
              core,
              promotion: undefined,
              shop,
            });
            return;
          }

          const promotion: LodgingPromotionInfo = {
            promotionStartDate: dayjs(
              lodging.nthNightPromotion.promotionStartDate
            ),
            promotionEndDate: dayjs(lodging.nthNightPromotion.promotionEndDate),
            nights: dayjs(params.stayDates.until).diff(
              dayjs(params.stayDates.from),
              "days"
            ),
            minNights: lodging.nthNightPromotion.los ?? 0,
            vibeTags: lodging.nthNightPromotion.vibeTags ?? [],
            isLocked: lodging.nthNightPromotion.isLocked,
          };

          if (lodging.available === false) {
            lodgings.push({
              state: PromotionAvailability.HotelUnavailable,
              core,
              promotion,
              shop,
            });
            return;
          }

          const searchFromDate = dayjs(params.stayDates.from);
          const searchUntilDate = dayjs(params.stayDates.until);
          const promotionStartDate = dayjs(
            lodging.nthNightPromotion.promotionStartDate
          );
          const promotionEndDate = dayjs(
            lodging.nthNightPromotion.promotionEndDate
          );

          const searchDatesIncluded =
            (searchFromDate.isAfter(promotionStartDate) ||
              searchFromDate.isSame(promotionStartDate)) &&
            (searchUntilDate.isBefore(promotionEndDate) ||
              searchUntilDate.isSame(promotionEndDate));

          const payAtHotelFee =
            lodging.price && "payAtHotelBreakdown" in lodging.price
              ? lodging.price.payAtHotelBreakdown?.total?.fiat
              : undefined;

          const propertyFee =
            payAtHotelFee && Math.round(payAtHotelFee.value) > 0
              ? payAtHotelFee
              : undefined;

          const { prices, rewards } = getPriceAndRewards(
            lodging,
            promotion.nights,
            propertyFee,
            1
          );

          if (
            (!searchDatesIncluded ||
              promotion.nights < promotion.minNights ||
              promotion.isLocked) &&
            prices &&
            rewards
          ) {
            lodgings.push({
              state: PromotionAvailability.PromotionUnavailable,
              core,
              prices,
              promotion,
              rewards: {
                totalPriceInMiles: rewards.value.toString(),
                rewardsCurrency: rewards.currency as RewardsCurrency,
              },
              shop,
            });
            return;
          }

          if (!prices || !rewards) {
            lodgings.push({
              state: PromotionAvailability.HotelUnavailable,
              core,
              promotion,
              shop,
            });
            return;
          }

          lodgings.push({
            state: PromotionAvailability.PromotionAvailable,
            core,
            prices,
            promotion,
            rewards: {
              totalPriceInMiles: rewards.value.toString(),
              rewardsCurrency: rewards.currency as RewardsCurrency,
            },
            shop,
          });
        });

        const filterCategories = getVibeTagFilterCategories(lodgings);

        return {
          state: OFFERS_STATES.SUCCESS,
          lodgings,
          shopNavigationParams,
          filterCategories,
        };
      }

      default:
        return ensureExhaustive(response);
    }
  }
);

export const getRewardsPropsForLodging = (
  rewardsCurrency: RewardsCurrency | undefined,
  rewardsAccount: RewardsAccount | undefined
): IRewards | undefined => {
  if (rewardsCurrency && rewardsAccount) {
    return {
      rewardsAccount,
      rewardCurrency: rewardsCurrency,
      rewardsUiVariant: "hotels" as const,
    };
  }
  return undefined;
};

export const getSearchedOfferTrackingProperties = createSelector(
  getOffersState,
  getIsUserTriggeredSearch,
  (offerState, isUserTriggeredSearch): SearchedPremiumOffersProperties => {
    if (offerState.state !== OFFERS_STATES.SUCCESS) {
      return {
        offer_unlocked_count: 0,
        offer_locked_count: 0,
        unavailable_count: 0,
        is_user_triggered: isUserTriggeredSearch,
        los: 0,
      };
    }
    const { lodgings } = offerState;

    // eslint-disable-next-line camelcase
    const offer_unlocked_count = lodgings.filter(
      (l) => l.state === PromotionAvailability.PromotionAvailable
    ).length;

    // eslint-disable-next-line camelcase
    const offer_locked_count = lodgings.filter(
      (l) => l.state === PromotionAvailability.PromotionUnavailable
    ).length;

    // eslint-disable-next-line camelcase
    const unavailable_count = lodgings.filter(
      (l) => l.state === PromotionAvailability.HotelUnavailable
    ).length;

    const los = offerState.shopNavigationParams.stayDates
      ? dayjs(offerState.shopNavigationParams.stayDates.until).diff(
          dayjs(offerState.shopNavigationParams.stayDates.from),
          "day"
        )
      : 0;

    return {
      // eslint-disable-next-line camelcase
      offer_unlocked_count,
      // eslint-disable-next-line camelcase
      offer_locked_count,
      // eslint-disable-next-line camelcase
      unavailable_count,
      is_user_triggered: isUserTriggeredSearch,
      los,
    };
  }
);
